import { supabase } from '@/lib/supabase/client';
import type { ScreenHistory, ScreenHistoryInsert } from '@/types/screen-history';

// Helper to get current user ID
async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

export const screenHistoryService = {
  async getByComponentInventoryId(componentInventoryId: string): Promise<ScreenHistory[]> {
    try {
      const { data, error } = await supabase
        .from('screen_history')
        .select('*')
        .eq('component_inventory_id', componentInventoryId)
        .order('event_date', { ascending: true });

      if (error) {
        console.error('Error fetching screen history:', error);
        return [];
      }

      return (data as ScreenHistory[]) || [];
    } catch (error) {
      console.error('Error fetching screen history:', error);
      return [];
    }
  },

  async getById(id: string): Promise<ScreenHistory | null> {
    try {
      const { data, error } = await supabase.from('screen_history').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching screen history:', error);
        return null;
      }

      return (data as ScreenHistory) || null;
    } catch (error) {
      console.error('Error fetching screen history:', error);
      return null;
    }
  },

  async create(data: ScreenHistoryInsert): Promise<ScreenHistory> {
    try {
      const userId = await getCurrentUserId();

      const insertData = {
        component_inventory_id: data.component_inventory_id,
        event_type: data.event_type,
        from_location_id: data.from_location_id ?? null,
        to_location_id: data.to_location_id ?? null,
        event_date: data.event_date,
        vendor: data.vendor ?? null,
        cost: data.cost ?? null,
        repair_type: data.repair_type ?? null,
        notes: data.notes ?? null,
        created_by: userId,
      };

      const { data: newHistory, error } = await supabase.from('screen_history').insert(insertData).select().single();

      if (error) {
        console.error('Error creating screen history:', error);
        throw new Error(`Failed to create screen history: ${error.message}`);
      }

      return newHistory as ScreenHistory;
    } catch (error) {
      console.error('Error creating screen history:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('screen_history').delete().eq('id', id);

      if (error) {
        console.error('Error deleting screen history:', error);
        throw new Error(`Failed to delete screen history: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting screen history:', error);
      throw error;
    }
  },
};

